import React, { useState, useEffect } from "react";
import Loader from "../../../Common/Loader/loader";
import { useLocation, useNavigate } from "react-router-dom";
import CustomDataTable from "../../../Common/ReportTable/ReportTable";
import ExcelJS from "exceljs";
import { Formik, Form, Field } from "formik";
import { Modal } from "react-bootstrap";
import { formatDateTimeWithoutSec, getCurrentDateTime1, getCurrentISTDate } from "../../../Common/helper/helpar";
import { toast } from "react-toastify";

const ViewDeployedTssctpReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data, fromDate, toDate } = location.state || {};
  const [isLoading, setIsLoading] = useState(false);
  const [scvList, setScvList] = useState(data || []);
  const [originalData, setOriginalData] = useState(data || []);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const dataPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);

  // Debugging: Log initial data
  useEffect(() => {
    console.log("Initial data from location.state:", data);
    console.log("fromDate:", fromDate, "toDate:", toDate);
  }, []);

  // Set default filter values
  const getDefaultFilterFields = () => {
    const from = fromDate ? new Date(fromDate) : new Date();
    const to = toDate ? new Date(toDate) : new Date();
    const defaultDate = new Date();
    const minDate = isNaN(from.getTime()) ? defaultDate : from;
    const maxDate = isNaN(to.getTime()) ? defaultDate : to;
    const currentDate = new Date();

    const formatDateTime = (date, isStart = true) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      if (!isStart && date.toDateString() === currentDate.toDateString()) {
        // Use current time for toDate if it's today
        const hours = String(currentDate.getHours()).padStart(2, "0");
        const minutes = String(currentDate.getMinutes()).padStart(2, "0");
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      }
      const hours = isStart ? "00" : "23";
      const minutes = isStart ? "00" : "59";
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    return {
      searchCircle: "",
      searchVehicleNumber: "",
      searchVehicleUID: "",
      searchTSName: "",
      searchFromDate: formatDateTime(minDate, true), // e.g., "2025-05-20T00:00"
      searchToDate: formatDateTime(maxDate, false), // e.g., "2025-05-22T11:03" if today
    };
  };

  // Set min and max for datetime-local inputs
  const from = fromDate ? new Date(fromDate) : new Date();
  const to = toDate ? new Date(toDate) : new Date();
  const defaultDate = new Date();
  const currentDate = new Date();
  const minDateTime = isNaN(from.getTime())
    ? `${defaultDate.getFullYear()}-${String(defaultDate.getMonth() + 1).padStart(2, "0")}-${String(defaultDate.getDate()).padStart(2, "0")}T00:00`
    : `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, "0")}-${String(from.getDate()).padStart(2, "0")}T00:00`;
  const maxDateTime = isNaN(to.getTime())
    ? `${defaultDate.getFullYear()}-${String(defaultDate.getMonth() + 1).padStart(2, "0")}-${String(defaultDate.getDate()).padStart(2, "0")}T${String(currentDate.getHours()).padStart(2, "0")}:${String(currentDate.getMinutes()).padStart(2, "0")}`
    : to.toDateString() === currentDate.toDateString()
    ? `${to.getFullYear()}-${String(to.getMonth() + 1).padStart(2, "0")}-${String(to.getDate()).padStart(2, "0")}T${String(currentDate.getHours()).padStart(2, "0")}:${String(currentDate.getMinutes()).padStart(2, "0")}`
    : `${to.getFullYear()}-${String(to.getMonth() + 1).padStart(2, "0")}-${String(to.getDate()).padStart(2, "0")}T23:59`;

  const parseScannedDate = (dateString) => {
    if (!dateString || dateString === "N/A") return null;
    // Try multiple formats
    const formats = [
      // DD-MM-YYYY HH:MM
      (str) => {
        const [datePart, timePart] = str.split(" ");
        if (!datePart || !timePart) return null;
        const [day, month, year] = datePart.split("-").map(Number);
        const [hour, minute] = timePart.split(":").map(Number);
        return new Date(year, month - 1, day, hour, minute);
      },
      // YYYY-MM-DD HH:MM:SS
      (str) => {
        const date = new Date(str.replace(" ", "T") + "Z");
        return isNaN(date.getTime()) ? null : date;
      },
      // ISO format (e.g., 2025-05-22T07:10:00)
      (str) => {
        const date = new Date(str);
        return isNaN(date.getTime()) ? null : date;
      },
      // DD/MM/YYYY HH:MM
      (str) => {
        const [datePart, timePart] = str.split(" ");
        if (!datePart || !timePart) return null;
        const [day, month, year] = datePart.split("/").map(Number);
        const [hour, minute] = timePart.split(":").map(Number);
        return new Date(year, month - 1, day, hour, minute);
      },
    ];

    for (const parse of formats) {
      const date = parse(dateString);
      if (date && !isNaN(date.getTime())) {
        console.log(`Parsed ${dateString} to ${date}`);
        return date;
      }
    }

    console.warn("Invalid scannedDate format:", dateString);
    return null;
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    fetchDataForPage(pageNumber);
  };

  const fetchDataForPage = (pageNumber) => {
    // Use data from location.state instead of fetching from API
    const initialData = data || [];
    setOriginalData(initialData); // Store unfiltered data
    setScvList(initialData);
    // Apply default filters
    searchReportedAtVehicleHandler(getDefaultFilterFields());
  };

  useEffect(() => {
    // Fetch initial data for the first page when the component mounts
    fetchDataForPage(currentPage);
  }, [fromDate, toDate]);

  const scvReportColumns = [
    {
      name: "S.No",
      selector: (row) => row["slno"],
      sortable: true,
      format: (row, rowIndex) => rowIndex + 1,
      width: '70px',
    },
     {
      name: " Deployed Corporation",
      selector: (row) => row["deployedcorp"],
      cell: (row) => <span>{row["deployedcorp"] || "N/A"}</span>,
      sortable: true,
      width: '200px',
    },
     {
      name: " Deployed Zone",
      selector: (row) => row["deployedZone"],
      cell: (row) => <span>{row["deployedZone"] || "N/A"}</span>,
      sortable: true,
      width: '160px',
    },
     {
      name: " Deployed Circle",
      selector: (row) => row["deployedCircle"],
      cell: (row) => <span>{row["deployedCircle"] || "N/A"}</span>,
      sortable: true,
      width: '160px',
    },
    {
      name: "Corporation",
      selector: (row) => row["corporationName"],
      cell: (row) => <span>{row["corporationName"] || "N/A"}</span>,
      sortable: true,
      width: '130px',
    },
    {
      name: "Zone",
      selector: (row) => row["zoneName"],
      cell: (row) => (
        <span
        // style={{ color: "blue", cursor: "pointer" }}
        //   onClick={() => handleZoneClick(row["zoneId"])}
        >
          {row["zoneName"]}
        </span>
      ),
      sortable: true,
        width: '120px',
    },
    {
      name: "Circle",
      selector: (row) => row["circleName"],
      cell: (row) => (
        <span
        // style={{ color: "blue", cursor: "pointer" }}
        //   onClick={() => handleZoneClick(row["zoneId"])}
        >
          {row["circleName"]}
        </span>
      ),
      sortable: true,
        width: '120px',
    },
    {
      name: "Ward",
      selector: (row) => row["wardName"],
      cell: (row) => (
        <span
        // style={{ color: "blue", cursor: "pointer" }}
        //   onClick={() => handleZoneClick(row["zoneId"])}
        >
          {row["wardName"] || "N/A"}
        </span>
      ),
      sortable: true,
        width: '120px',
    },
    {
      name:  <>Vehicle Type </>,
      selector: (row) => row["vehicleTypeName"],
      cell: (row) => (
        <span >{row["vehicleTypeName"] || "N/A"}</span>
      ),
      sortable: true,
        width: '120px',
    },
    {
      name:  <>Owner Type </>,
      selector: (row) => row["ownerTypeName"],
      cell: (row) => (
        <span >{row["ownerTypeName"] || "N/A"}</span>
      ),
      sortable: true,
        width: '120px',
    },
    {
      name:  <>Vehicle Capacity </>,
      selector: (row) => row["vehicleCapacityName"],
      cell: (row) => (
        <span >{row["vehicleCapacityName"] || "N/A"}</span>
      ),
      sortable: true,
        width: '120px',
    },
    {
      name: "TS/SCTP",
      selector: (row) => row["transferStationName"],
      cell: (row) => (
        <span > {row["transferStationName"] || "N/A"}</span>
      ),
      sortable: true,
        width: '120px',
    },
    {
      name:  <>Vehicle Number </>,
      selector: (row) => row["vehicleNumber"],
      cell: (row) => (
        <span > {row["vehicleNumber"] || "N/A"}</span>
      ),
      sortable: true,
        width: '120px',
    },
    {
      name:  <>Vehicle UID </>,
      selector: (row) => row["vehicleUuid"],
      cell: (row) => (
        <span >{row["vehicleUuid"] || "N/A"}</span>
      ),
      sortable: true,
        width: '120px',
    },
    // {
    //   name: "Container Uuid",
    //   selector: (row) => (
    //     <span >{row["containerUuid"]}</span>
    //   ),
    //   sortable: true,
    // },
    {
      name:  <>Scanned Date </>,
      selector: (row) => row["scannedDate"],
      cell: (row) => (
        <span >{row["scannedDate"] || "N/A"}</span>
      ),
      sortable: true,
        width: '120px',
    },
    {
      name: "Latitude",
      selector: (row) => row["scannedLatitude"],
      cell: (row) => (
        <span >{row["scannedLatitude"] || "N/A"}</span>
      ),
      sortable: true,
        width: '120px',
    },
    {
      name: "Longitude",
      selector: (row) => row["scannedLongitude"],
      cell: (row) => (
        <span >{row["scannedLongitude"] || "N/A"}</span>
      ),
      sortable: true,
        width: '120px',
    },
    {
      name: "Vehicle Image",
      cell: (row) => {
        if (row["vehicleImageUrl"]) {
          return (
            <img
              src={row["vehicleImageUrl"]}
              alt="Vehicle"
              style={{ width: "100px", height: "50px", objectFit: "cover", borderRadius: "10px", cursor: "pointer" }}
              onClick={() => handleImageClick(row.vehicleImageUrl)}
            />
          );
        } else {
          return <span style={{ color: "blue" }}>No image</span>;
        }
      },
      sortable: true,
    },
  ];
  const searchReportedAtVehicleHandler = (values) => {
    console.log("Filter values:", values);
    const termCircle = values.searchCircle ? values.searchCircle.toLowerCase() : "";
    const termVehicle = values.searchVehicleNumber ? values.searchVehicleNumber.toLowerCase() : "";
    const termVehicleUID = values.searchVehicleUID ? values.searchVehicleUID.toLowerCase() : "";
    const termTSName = values.searchTSName ? values.searchTSName.toLowerCase() : "";
    const termFromDate = values.searchFromDate ? new Date(values.searchFromDate) : null;
    const termToDate = values.searchToDate ? new Date(values.searchToDate) : null;

    // Validate date range
    if (termFromDate && termToDate && termToDate < termFromDate) {
      toast.error("To Date must be after From Date");
      return;
    }

    console.log("originalData",originalData)

    // const filteredData = (Array.isArray(originalData) ? originalData : []).filter((item) => {
    //   const circleName = item?.circleName ? item.circleName.toLowerCase() : "";
    //   const vehicleNumber = item?.vehicleNumber ? item.vehicleNumber.toLowerCase() : "";
    //   const vehicleUuid = item?.vehicleUuid ? item.vehicleUuid.toLowerCase() : "";
    //   const transferStationName = item?.transferStationName ? item.transferStationName.toLowerCase() : "";
    //   const scannedDate = item?.scannedDate ? parseScannedDate(item.scannedDate) : null;

    //   const matchesCircle = !termCircle || circleName.includes(termCircle);
    //   const matchesVehicle = !termVehicle || vehicleNumber.includes(termVehicle);
    //   const matchesVehicleUID = !termVehicleUID || vehicleUuid.includes(termVehicleUID);
    //   const matchesTSName = !termTSName || transferStationName.includes(termTSName);
    //   const matchesDate =
    //     !scannedDate ||
    //     (!termFromDate && !termToDate) ||
    //     (termFromDate && !termToDate && scannedDate >= termFromDate) ||
    //     (!termFromDate && termToDate && scannedDate <= termToDate) ||
    //     (termFromDate && termToDate && scannedDate >= termFromDate && scannedDate <= termToDate);

    //   return matchesCircle && matchesVehicle && matchesVehicleUID && matchesTSName
    //   //  && matchesDate;
    // });

    const filteredData = (Array.isArray(originalData) ? originalData : []).filter((item) => {

  const circleName = item?.circleName?.toLowerCase() || "";
  const vehicleNumber = item?.vehicleNumber?.toLowerCase() || "";
  const vehicleUuid = item?.vehicleUuid?.toLowerCase() || "";
  const transferStationName = item?.transferStationName?.toLowerCase() || "";

  const scannedDate = item?.scannedDate
    ? parseScannedDate(item.scannedDate)
    : null;

  const termCircleLower = termCircle?.toLowerCase().trim() || "";
  const termVehicleLower = termVehicle?.toLowerCase().trim() || "";
  const termVehicleUIDLower = termVehicleUID?.toLowerCase().trim() || "";
  const termTSNameLower = termTSName?.toLowerCase().trim() || "";

  const matchesCircle =
    !termCircleLower || circleName.includes(termCircleLower);

  const matchesVehicle =
    !termVehicleLower || vehicleNumber.includes(termVehicleLower);

  const matchesVehicleUID =
    !termVehicleUIDLower || vehicleUuid.includes(termVehicleUIDLower);

  const matchesTSName =
    !termTSNameLower || transferStationName.includes(termTSNameLower);

  return matchesCircle && matchesVehicle && matchesVehicleUID && matchesTSName;
});
    console.log("Filtered data:", filteredData);
    setScvList(filteredData);
  };

  const handleReset = (resetForm) => {
    const defaultValues = getDefaultFilterFields();
    resetForm({ values: defaultValues });
    searchReportedAtVehicleHandler(defaultValues);
  };

  const handleBackClick = () => {
    navigate(-1, { state: { from: fromDate, to: toDate } });
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  const handleClose = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  const downloadAsExcel = async () => {
    const currentDate = new Date();
    const formattedDate = `${currentDate.getDate()}-${currentDate.getMonth() + 1}-${currentDate.getFullYear()}`;
    const formattedTime = `${currentDate.getHours()}:${currentDate.getMinutes()}:${currentDate.getSeconds()}`;
    const cumulativeHeadingReport = `Vehicle Wise SCV's Deployment & Trips Performance Report on ${fromDate ? formatDateTimeWithoutSec(fromDate) : getCurrentISTDate()} to ${toDate ? formatDateTimeWithoutSec(toDate) : getCurrentISTDate()} (at ${getCurrentDateTime1()})`;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Zones");

    const boldFont = { bold: true };

    const downloadDateTime = `Reported on: ${formattedDate} ${formattedTime}`;
    const dateTimeRow = worksheet.addRow(["", "", downloadDateTime, "", "", "", "", "", "", "", "", "", "", "", "", "", ""]);
    worksheet.mergeCells(1, 3, 1, 17);
    dateTimeRow.getCell(3).font = boldFont;
    dateTimeRow.getCell(3).alignment = { horizontal: "center" };
    dateTimeRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      if (colNumber >= 3 && colNumber <= 17) {
        cell.font = boldFont;
        cell.alignment = { horizontal: "center" };
      }
    });

    const cumulativeHeadingRow = worksheet.addRow(["", "", cumulativeHeadingReport, "", "", "", "", "", "", "", "", "", "", "", "", "", ""]);
    worksheet.mergeCells(2, 3, 2, 17);
    cumulativeHeadingRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      if (colNumber >= 3 && colNumber <= 17) {
        cell.font = boldFont;
        cell.alignment = { horizontal: "center" };
      }
    });

    worksheet.addRow([""]);

    const tableHeaderRow = worksheet.addRow([
      "S.No",
      "Deployed Corporation",
      "Deployed Zone",
      "Deployed Circle",
      "Corporation",
      "Zone",
      "Circle",
      "Ward",
      "Vehicle Type",
      "Owner Type",
      "Vehicle Capacity",
      "TransferStation Name",
      "Vehicle Number",
      "Vehicle UID",
      "Scanned Date",
      "Latitude",
      "Longitude",
    ]);

    tableHeaderRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      cell.font = boldFont;
      cell.alignment = { horizontal: "left" };
      if (colNumber >= 1 && colNumber <= 17) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "F6B1B1" },
        };
      }
    });

    scvList.forEach((item, index) => {
      worksheet.addRow([
        index + 1,
        item.deployedcorp ?? "N/A",
        item.deployedZone ?? "N/A",
        item.deployedCircle ?? "N/A",
        item.corporationName ?? "N/A",
        item.zoneName ?? "N/A",
        item.circleName ?? "N/A",
        item.wardName ?? "N/A",
        item.vehicleTypeName ?? "N/A",
        item.ownerTypeName ?? "N/A",
        item.vehicleCapacityName ?? "N/A",
        item.transferStationName ?? "N/A",
        item.vehicleNumber ?? "N/A",
        item.vehicleUuid ?? "N/A",
        item.scannedDate ?? "N/A",
        item.scannedLatitude ?? "N/A",
        item.scannedLongitude ?? "N/A",
      ]).eachCell({ includeEmpty: true }, (cell, colNumber) => {
        cell.alignment = { horizontal: "left" };
      });
    });

    worksheet.columns = [
      { width: 5 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "DeployedSCVsTripsReport.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div style={{ width: "100%" }}>
        <div className="content">
          <div className="page-content">
            <div className="card mt-3">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
                  <Formik initialValues={getDefaultFilterFields()}>
                    {({ handleChange, values, resetForm }) => (
                      <Form className="d-flex align-items-center">
                        <div className="row">
                          <div className="col-md-3">
                            <div className="form-group">
                              <Field
                                name="searchCircle"
                                placeholder="Search Circle"
                                type="text"
                                className="form-control bg-white"
                                onInput={(e) => {
                                  handleChange(e);
                                  searchReportedAtVehicleHandler({ ...values, searchCircle: e.target.value });
                                }}
                              />
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="form-group">
                              <Field
                                name="searchVehicleNumber"
                                placeholder="Search Vehicle Number"
                                type="text"
                                className="form-control bg-white"
                                onInput={(e) => {
                                  handleChange(e);
                                  searchReportedAtVehicleHandler({ ...values, searchVehicleNumber: e.target.value });
                                }}
                              />
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="form-group">
                              <Field
                                name="searchVehicleUID"
                                placeholder="Search Vehicle UID"
                                type="text"
                                className="form-control bg-white"
                                onInput={(e) => {
                                  handleChange(e);
                                  searchReportedAtVehicleHandler({ ...values, searchVehicleUID: e.target.value });
                                }}
                              />
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="form-group">
                              <Field
                                name="searchTSName"
                                placeholder="Search TS/SCTP"
                                type="text"
                                className="form-control bg-white"
                                onInput={(e) => {
                                  handleChange(e);
                                  searchReportedAtVehicleHandler({ ...values, searchTSName: e.target.value });
                                }}
                              />
                            </div>
                          </div>
                          {/* <div className="col-md-3">
                            <div className="form-group">
                              <Field
                                name="searchFromDate"
                                placeholder="From Date and Time"
                                type="datetime-local"
                                className="form-control bg-white"
                                min={minDateTime}
                                max={maxDateTime}
                                onInput={(e) => {
                                  handleChange(e);
                                  searchReportedAtVehicleHandler({ ...values, searchFromDate: e.target.value });
                                }}
                              />
                            </div>
                          </div>
                          <div className="col-md-2">
                            <div className="form-group">
                              <Field
                                name="searchToDate"
                                placeholder="To Date and Time"
                                type="datetime-local"
                                className="form-control bg-white"
                                min={minDateTime}
                                max={maxDateTime}
                                onInput={(e) => {
                                  handleChange(e);
                                  searchReportedAtVehicleHandler({ ...values, searchToDate: e.target.value });
                                }}
                              />
                            </div>
                          </div> */}
                        </div>
                      </Form>
                    )}
                  </Formik>
                  <div className="d-flex" style={{marginBottom:'20px'}}>
                    <button className="btn btn-secondary" onClick={handleBackClick}>
                      Back
                    </button>
                    <button className="btn btn-excel" onClick={downloadAsExcel}>
                      <i className="fa-solid fa-file-excel me-1 icon-container"></i> Excel
                    </button>
                  </div>
                </div>

                <div className="top-header">
                  <span>
                    Vehicle Wise SCV's Deployment & Trips Performance Report on {fromDate ? formatDateTimeWithoutSec(fromDate) : getCurrentISTDate()} to {toDate ? formatDateTimeWithoutSec(toDate) : getCurrentISTDate()} (at {getCurrentDateTime1()})
                  </span>
                </div>

                <CustomDataTable
                  data={scvList}
                  columns={scvReportColumns}
                  pagination
                  dataPerPage={dataPerPage}
                  currentPage={currentPage}
                  handlePageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {isLoading && <Loader />}
      <Modal show={showImageModal} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Image Full Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <img src={selectedImage} alt="Full vehicle" style={{ width: "100%", height: "400px", borderRadius: "5px", objectFit: "contain" }} />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ViewDeployedTssctpReport;